import { useEffect, useRef, useMemo } from "react";
import { calculateDeliveryCostNonmetallic } from "./calculateDeliveryNonmetallic";
import { GLOBAL_CONSTANTS } from "../../constants";

const PATH_TO_BACKEND = process.env.REACT_APP_PATH_TO_BACKEND;

const REGION_MAP = {
  "Санкт-Петербург": "СПБ",
  "Ленинградская область": "СПБ",
  "Москва": "МСК",
  "Московская область": "МСК",
  "Краснодар": "КРД",
  "Краснодарский край": "КРД",
};

const REGION_COORDS = {
  "СПБ": { lat: 59.9343, lon: 30.3351 },
  "МСК": { lat: 55.7512, lon: 37.6184 },
  "КРД": { lat: 45.0355, lon: 38.9753 },
};

function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

async function determineRegion(addressAndCoordinates) {
  try {
    const { address, coordinates } = addressAndCoordinates || {};
    let regionFullName = null;

    // Геокодирование идёт через наш бэкенд (/api/geocode-region), а не прямо
    // в Nominatim из браузера — у Nominatim жёсткий анти-абьюз лимит, при
    // блокировке/зависании fetch без таймаута навсегда вешал весь расчёт.
    // Таймаут здесь — подстраховка на случай проблем с нашим же бэкендом.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 6000);

    try {
      if (coordinates?.latitude && coordinates?.longitude) {
        const response = await fetch(
          `${PATH_TO_BACKEND}/api/geocode-region?lat=${coordinates.latitude}&lon=${coordinates.longitude}`,
          { signal: controller.signal }
        );
        const data = await response.json();
        regionFullName = data.regionFullName || null;
      } else if (address) {
        const response = await fetch(
          `${PATH_TO_BACKEND}/api/geocode-region?address=${encodeURIComponent(address)}`,
          { signal: controller.signal }
        );
        const data = await response.json();
        regionFullName = data.regionFullName || null;
      }
    } finally {
      clearTimeout(timeoutId);
    }

    if (!regionFullName) {
      console.log("🔹 Регион не определен.");
      return null;
    }

    const region = REGION_MAP[regionFullName];
    if (region) {
      console.log("🔹 Регион найден в списке:", region);
      return region;
    }

    if (coordinates?.latitude && coordinates?.longitude) {
      let closestRegion = null;
      let minDistance = Infinity;
      for (const [reg, coords] of Object.entries(REGION_COORDS)) {
        const distance = calculateDistance(
          coordinates.latitude,
          coordinates.longitude,
          coords.lat,
          coords.lon
        );
        if (distance < minDistance) {
          minDistance = distance;
          closestRegion = reg;
        }
      }
      console.log(`🔹 Неизвестный регион "${regionFullName}", ближайший: ${closestRegion} (${minDistance.toFixed(2)} км)`);
      return closestRegion;
    }

    console.log(`🔹 Неизвестный регион "${regionFullName}", координаты отсутствуют, возвращаем полное название`);
    return regionFullName;
  } catch (error) {
    console.error("Ошибка при определении региона:", error);
    return null;
  }
}

const BackendProductionsNonmetallic = ({
  products,
  paymentMethod,
  deliveryMethod,
  addressAndCoordinates,
  selfPickUp,
  setComparisons,
  setIsLoading,
  selectedRegion,
  setAddressValidationError,
  setProductErrors
}) => {
  const deliveryCache = useRef(new Map()).current;
  const requestControllerRef = useRef(null);
  const requestSeqRef = useRef(0);

  // Мемоизируем ключевые данные для пересчета
  const productsKey = useMemo(() =>
    products?.map(p => ({ name: p.name, quantity: p.quantity })) || [],
    [products]
  );

  useEffect(() => {
    // Игнорируем временные изменения (с флагом _isTemporary)
    const hasTemporaryChanges = products.some(product => product._isTemporary);
    if (hasTemporaryChanges) {
      return;
    }

    console.log("🔍 BackendProductionsNonmetallic useEffect СРАБОТАЛ");
    console.log("🔍 BackendProductionsNonmetallic: products=", products);
    console.log("🔍 BackendProductionsNonmetallic: deliveryMethod=", deliveryMethod);
    console.log("🔍 BackendProductionsNonmetallic: deliveryMethod type=", typeof deliveryMethod);
    console.log("🔍 BackendProductionsNonmetallic: deliveryMethod stringified=", JSON.stringify(deliveryMethod));
    if (requestControllerRef.current) {
      requestControllerRef.current.abort();
    }
    const controller = new AbortController();
    requestControllerRef.current = controller;
    const requestSeq = ++requestSeqRef.current;

    const fetchData = async () => {
      if (!Array.isArray(products) || !products.length || products.some(p => !p.name)) {
        console.log("🔍 BackendProductionsNonmetallic: products невалидны, сбрасываем comparisons");
        setComparisons([]);
        setIsLoading?.(false);
        return;
      }

      console.log("🔍 BackendProductionsNonmetallic: products валидны, начинаем fetchData");
      setIsLoading?.(true);

      // валидация координат -----
      const { latitude, longitude } = addressAndCoordinates?.coordinates || {};
      const isAddressProvided = addressAndCoordinates?.address && addressAndCoordinates.address.trim() !== '';

      const hasValidCoords = latitude != null && longitude != null && !isNaN(latitude) && !isNaN(longitude);

      if (!hasValidCoords) {
        const errorMessage = isAddressProvided
          ? 'Адрес не распознан. Пожалуйста, укажите координаты вручную.'
          : 'Не удалось определить местоположение по координатам. Проверьте введённые данные.';
        if (setAddressValidationError) setAddressValidationError(errorMessage);
        setIsLoading?.(false);
        return;
      } else {
        // Если координаты валидны, сбрасываем предыдущую ошибку
        if (setAddressValidationError) setAddressValidationError(null);
      }
      // ----- валидация координат -----

      try {
        const region = selfPickUp ? selectedRegion : await determineRegion(addressAndCoordinates);
        console.log("🔹 Используемый регион:", region);

        // Создаем копию products, заменяя null на 1 для расчетов
        const productsForCalc = products.map(product => ({
          ...product,
          quantity: product.quantity == null || product.quantity === 0 ? 1 : product.quantity,
        }));

        // Фильтруем только нерудные материалы на уровне запроса
        const response = await fetch(`${PATH_TO_BACKEND}/api/filter`, {
          signal: controller.signal,
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            names: productsForCalc.map(p => p.name).filter(Boolean),
            region,
            paymentMethod,
            // Добавляем фильтр только для нерудки
            onlyNonmetallic: true,
          }),
        });

        if (!response.ok) throw new Error(`Ошибка: ${response.status}`);
        const { data } = await response.json();
        if (controller.signal.aborted || requestSeq !== requestSeqRef.current) {
          return;
        }

        // Фильтруем производства, которые имеют нерудные материалы
        const validManufacturers = Object.entries(data || {})
          .filter(([, { products: manufacturerProducts }]) => {
            if (!manufacturerProducts) return false;

            // Проверяем, что есть нерудные материалы
            return Object.values(manufacturerProducts).some(rows =>
              rows?.length > 0 && rows.some(row => row.CALCULATOR === "Нерудные материалы")
            );
          });

        if (!validManufacturers.length) {
          console.log("🔍 BackendProductionsNonmetallic: нерудных производств не найдено, сбрасываем comparisons");
          setComparisons([]);
          setIsLoading?.(false);
          return;
        }

        const newComparisons = await Promise.all(
          validManufacturers.map(async ([manufacturer, { products: manufacturerProducts, productionInfo }]) => {
            const deliveryManufacturer = productionInfo?.NAME || manufacturer;
            const displayManufacturer = productionInfo?.NAME || manufacturer;
            const displayMaterials = productsForCalc.flatMap(product => {
              const productRows = manufacturerProducts[product.name] || [];
              // Фильтруем только нерудные материалы
              const nonmetallicRows = productRows.filter(row => row.CALCULATOR === "Нерудные материалы");

              console.log(`🔍 ${product.name}: ${nonmetallicRows.length} нерудных записей из ${manufacturer}`);
              return nonmetallicRows.map(productData => ({
                calculator: productData.CALCULATOR || "Нет данных",
                category: productData.CATEGORY || "Нет данных",
                name: product.name,
                productId: product.id,
                quantity: Number(product.quantity) || 0,
                unit: productData.BASE_UNIT || "м³",
                pricePerOne: Number((paymentMethod === "cash"
                  ? productData.PRICE_CASH
                  : productData.PRICE_NONCASH)?.toString().replace(/\s/g, "")) || 0,
                basePricePerOne: Number((paymentMethod === "cash"
                  ? productData.PRICE_CASH
                  : productData.PRICE_NONCASH)?.toString().replace(/\s/g, "")) || 0,
                date: productData.PRICE_UPDATE_DATE || "Нет данных",
                description: productData.DESCRIPTION || "Нет данных",
                massPerOne: Number(productData.WEIGHT_TONS?.toString().replace(",", ".")) || 0,
                volumePerOne: Number(productData.VOLUME_CUBIC?.toString().replace(",", ".")) || 1,
                manufacturer: deliveryManufacturer,
                numberPallets: Math.ceil(
                  (Number(product.quantity) || 0) /
                  (Number(productData.PALLET_UNITS?.replace(/,/g, ".")) || 1)
                ),
                palletUnits: Number(productData.PALLET_UNITS?.replace(/,/g, ".")) || 1,
                bitrixId: productData.BITRIX_ID || "",
                source: productData.SOURCE || "-",
                // Добавляем поля для поиска аналогов песка
                mkr: productData.MKR || null,
                kf: productData.KF || null,
                class: productData.CLASS || null,
              }));
            }).filter(Boolean);

            // Если нет нерудных материалов, пропускаем
            if (displayMaterials.length === 0) {
              return null;
            }

            const deliveryMaterials = productsForCalc.map(product => {
              const productRows = manufacturerProducts[product.name] || [];
              const nonmetallicRows = productRows.filter(row => row.CALCULATOR === "Нерудные материалы");
              if (!nonmetallicRows.length) return null;

              // Выбираем запись с ANAL_MKR и ANAL_KF, если она есть, иначе первую
              const rowWithAnalParams = nonmetallicRows.find(row => row.ANAL_MKR != null && row.ANAL_KF != null);
              const selectedRow = rowWithAnalParams || nonmetallicRows[0];

              return {
                calculator: selectedRow.CALCULATOR || "Нерудные материалы",
                category: selectedRow.CATEGORY || "Нет данных",
                name: product.name,
                quantity: product.quantity || 0,
                unit: selectedRow.BASE_UNIT || "м³",
                pricePerOne: Number((paymentMethod === "cash"
                  ? selectedRow.PRICE_CASH
                  : selectedRow.PRICE_NONCASH)?.toString().replace(/\s/g, "")) || 0,
                basePricePerOne: Number((paymentMethod === "cash"
                  ? selectedRow.PRICE_CASH
                  : selectedRow.PRICE_NONCASH)?.toString().replace(/\s/g, "")) || 0,
                date: selectedRow.PRICE_UPDATE_DATE || "Нет данных",
                description: selectedRow.DESCRIPTION || "Нет данных",
                massPerOne: Number(selectedRow.WEIGHT_TONS?.toString().replace(",", ".")) || 0,
                volumePerOne: Number(selectedRow.VOLUME_CUBIC?.toString().replace(",", ".")) || 1,
                manufacturer: deliveryManufacturer,
                numberPallets: Math.ceil(
                  (product.quantity || 0) / (Number(selectedRow.PALLET_UNITS?.replace(/,/g, ".")) || 1)
                ),
                palletUnits: Number(selectedRow.PALLET_UNITS?.replace(/,/g, ".")) || 1,
                bitrixId: selectedRow.BITRIX_ID || "",
                source: selectedRow.SOURCE || "-",
                // Добавляем поля для поиска аналогов песка
                mkr: selectedRow.MKR || null,
                kf: selectedRow.KF || null,
                class: selectedRow.CLASS || null,
              };
            }).filter(Boolean);

            console.log(`🚚 deliveryMaterials для ${manufacturer} (Нерудка):`, deliveryMaterials);

            const cacheKey = JSON.stringify({
              materials: deliveryMaterials,
              deliveryMethod,
              productionInfo,
              selfPickUp,
              addressAndCoordinates,
              paymentMethod,
              nonmetallic: true, // Добавляем флаг для кэша
            });

            console.log(`🗂️ cacheKey для ${manufacturer}:`, cacheKey.substring(0, 100) + '...');
            console.log(`🗂️ Cache HIT для ${manufacturer}:`, deliveryCache.has(cacheKey));

            if (deliveryCache.has(cacheKey)) {
              console.log(`🗂️ Используем кэш для ${manufacturer}`);
              return {
                id: manufacturer,
                production: displayManufacturer,
                materials: displayMaterials,
                productionInfo,
                delivery: deliveryCache.get(cacheKey),
              };
            }

            const delivery = selfPickUp
              ? {
                ner: {
                  category: "Нерудные материалы",
                  details: { data: { totalCost: 0, numberOfTrips: 0, costPerTrip: 0, bestMethod: "Самовывоз" } },
                }
              }
              : await calculateDeliveryCostNonmetallic(deliveryMaterials, deliveryMethod, productionInfo, selfPickUp, addressAndCoordinates, region, paymentMethod, controller.signal);

            if (controller.signal.aborted || requestSeq !== requestSeqRef.current) {
              return null;
            }

            let hasDeliveryError = false;
            let errorMessage = '';

            if (delivery && typeof delivery === 'object') {
              const nerDelivery = delivery.ner;
              if (nerDelivery && nerDelivery.error === true) {
                hasDeliveryError = true;
                errorMessage = nerDelivery.details?.error || 'Ошибка при расчёте доставки нерудных материалов';
              }
            }

            if (hasDeliveryError && displayMaterials.length && setProductErrors) {
              const errors = {};
              displayMaterials.forEach(mat => {
                // Ищем товар по уникальному ID (аналогично тому, как делаем в BackendProductions.js)
                const product = products.find(p => p.id === mat.productId);
                if (product) {
                  errors[product.id] = errorMessage;
                }
              });
              setProductErrors(prev => ({ ...prev, ...errors }));
            }

            console.log(`🚚 Расчет доставки для ${manufacturer} завершен:`, delivery);
            deliveryCache.set(cacheKey, delivery);
            return { id: manufacturer, production: displayManufacturer, materials: displayMaterials, productionInfo, delivery };
          })
        );

        // Фильтруем null значения (производства без нерудки)
        const filteredComparisons = newComparisons.filter(Boolean);

        console.log("🔍 BackendProductionsNonmetallic: newComparisons=", filteredComparisons);
        if (!controller.signal.aborted && requestSeq === requestSeqRef.current) {
          setComparisons(filteredComparisons);
        }
      } catch (err) {
        if (err?.name === "AbortError") {
          return;
        }
        console.error("❌ Ошибка:", err);
        if (requestSeq === requestSeqRef.current) {
          setComparisons([]);
        }
      } finally {
        if (requestSeq === requestSeqRef.current) {
          setIsLoading?.(false);
        }
      }
    };

    fetchData();

    return () => {
      controller.abort();
    };
  }, [
    // Оптимизированные зависимости - только критичные для пересчета
    JSON.stringify(productsKey),
    paymentMethod,
    JSON.stringify(deliveryMethod),
    JSON.stringify(addressAndCoordinates),
    selfPickUp,
    selectedRegion,
    setComparisons,
    setIsLoading, 
  ]);

  return null;
};

export default BackendProductionsNonmetallic;
